/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useLayoutEffect, useState } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	ContentComponentContextProvider,
	type ContentComponentProps,
} from '../../hooks/useContentComponentContext';
import { GlobalWrapper } from '../GlobalWrapper/GlobalWrapper';
import { SessionUIComponent } from '../SessionUI/SessionUIComponent';

import { ContentComponent } from './ContentComponent';

const useSharedPluginStateNoDebounce = (api: ContentComponentProps['api']) => {
	const [state, setState] = useState(
		api?.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState
			.modalMountedTimeStamp,
	);
	useLayoutEffect(() => {
		const unsub = api?.aiExperience?.sharedState.onChange(
			({ prevSharedState, nextSharedState }) => {
				if (
					prevSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp !==
					nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp
				) {
					setState(nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp);
				}
			},
		);

		return () => {
			unsub?.();
		};
	}, [api]);

	return state;
};

export const ContentComponentWrapper = (
	props: Omit<ContentComponentProps, 'isRovoLoading' | 'setIsRovoLoading'>,
) => {
	const modalMountedTimeStamp = useSharedPluginStateNoDebounce(props.api);
	const targetId = useSharedPluginStateWithSelector(
		props.api,
		['aiExperience'],
		(state) => state.aiExperienceState?.aiDecorationExperiencePluginState.targetId,
	);

	/**
	 * This is to detect the state of the Rovo prompt and loading screens
	 * from RovoInlineChatPrompt used in RovoPromptScreenWithLogic,
	 * as it does not respect the state value from XState.
	 */
	const [isRovoLoading, setIsRovoLoading] = useState(false);

	/* reset the component when start AI again, so that the error boundary will also be reset */
	return (
		<GlobalWrapper>
			<ContentComponentContextProvider
				value={{ ...props, isRovoLoading, setIsRovoLoading, targetId }}
			>
				<ContentComponent key={modalMountedTimeStamp} />
				{fg('platform_editor_ai_aifc_patch_beta_2') && (
					<SessionUIComponent
						api={props.api}
						providerFactory={props.providerFactory}
						appearance={props.appearance}
					/>
				)}
			</ContentComponentContextProvider>
		</GlobalWrapper>
	);
};
